import { mdiChevronLeft } from '@mdi/js';
import { Icon } from '@mdi/react';
import { commonButtonMessages, signatureAreaMessages, SignatureDocument, SignatureDocumentModule, SignatureDocumentServiceActivationModule } from 'azshared';
import React, { Fragment, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { AppButton, AppButtonVariant } from '../../../../../components/buttons/app-button';
import { AppCheckbox } from '../../../../../components/checkbox/app-checkbox';
import { AppSvgIcon } from '../../../../../components/svg-icon/app-svg-icon';
import { SignatureAreaLink } from '../signature-area-link/signature-area-link';
import './signature-area-base-document-module.scss';

export const SignatureAreaBaseDocumentModule = (props: {
  document: SignatureDocument;
  module: SignatureDocumentModule;
  isFirstModule: boolean;
  isSecondStep: boolean;
  acknowledgementChecked: boolean;
  isLoadingRejectProposal: boolean;
  isLoadingGetUrlForSignature: boolean;
  openDetail: (moduleId: string, documentId: string) => void;
  clickGoBack: () => void;
  clickGoForward: () => void;
  clickRejected: () => void;
  onAcknowledgementChanged: (checked: boolean) => void;
  isProspect: boolean;
}) => {
  const {
    document,
    openDetail,
    module,
    isFirstModule,
    isSecondStep,
    clickGoBack,
    clickGoForward,
    clickRejected,
    onAcknowledgementChanged,
    acknowledgementChecked,
    isLoadingGetUrlForSignature,
    isLoadingRejectProposal
  } = props;
  const { formatMessage } = useIntl();

  const handleClick = useCallback(
    (moduleId: string) => {
      openDetail(moduleId, document.id);
    },
    [document.id, openDetail]
  );

  const onInfoClick = useCallback(() => {
    handleClick(module.id);
  }, [module.id, handleClick]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        clickGoBack();
      }
    },
    [clickGoBack]
  );

  return (
    <Fragment>
      {!props.isProspect ? (
        <div className='signature-area-base-document-module'>
          <div className='signature-area-base-document-module-title'>
            {!isFirstModule && <span>-</span>}
            <SignatureAreaLink module={module} />
            {(module as SignatureDocumentServiceActivationModule).moduleInformation?.length > 0 && (
              <AppSvgIcon key={`icon-${module.id}`} icon='info' onClick={onInfoClick} />
            )}
          </div>
          {!isFirstModule &&
            (module as SignatureDocumentServiceActivationModule)?.moduleOperation &&
            (module as SignatureDocumentServiceActivationModule)?.moduleOperation.length !== 0 && (
              <div>
                {(module as SignatureDocumentServiceActivationModule)?.moduleOperation?.map((op) => (
                  <div key={op.product} className='signature-area-base-document-module-title'>
                    {!isFirstModule && <span> </span>}
                    {op.kidUrl && (
                      <a className='app-link' href={`${op.kidUrl}`} target='_blank' rel='noreferrer'>
                        {formatMessage(signatureAreaMessages.kidLabel)}
                        {' - '}
                        {op.product}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          {isFirstModule && module.type !== 'QuestionnaireModule' && (
            <div className='signature-area-base-document-module-disclaimer'>
              {formatMessage(isSecondStep ? signatureAreaMessages.maxFundDisclaimer : signatureAreaMessages.basicConsultingDisclaimer)}
            </div>
          )}
          {isFirstModule && (
            <div className='signature-area-base-document-module-buttons'>
              {module.type !== 'QuestionnaireModule' && (
                <AppCheckbox
                  label={formatMessage(signatureAreaMessages.acknowledgmentLabel)}
                  checked={!document.isMySignatureRequired || acknowledgementChecked}
                  disabled={!document.isMySignatureRequired || isLoadingGetUrlForSignature}
                  onChange={onAcknowledgementChanged}
                />
              )}
              {isSecondStep && (
                <a role='button' rel='noopener noreferrer' className='back' onClick={clickGoBack} onKeyDown={handleKeyDown} tabIndex={0}>
                  <Icon path={mdiChevronLeft} size={0.8} />
                  <span>{formatMessage(commonButtonMessages.backLink)}</span>
                </a>
              )}
              {((document.type?.practiceType === 'InvestmentProposal' && document.type?.proposalType === 'Advanced') ||
                document.type?.practiceType === 'MAX') && (
                <AppButton
                  onClick={clickRejected}
                  variant={AppButtonVariant.Secondary}
                  disabled={!document.isMySignatureRequired || isLoadingGetUrlForSignature}
                  label={formatMessage(signatureAreaMessages.rejectLabel)}
                />
              )}
              <AppButton
                label={formatMessage(!document.isMySignatureRequired ? signatureAreaMessages.signedLabel : signatureAreaMessages.forwardLabel)}
                disabled={!document.isMySignatureRequired || (!acknowledgementChecked && module.type !== 'QuestionnaireModule') || isLoadingRejectProposal}
                onClick={clickGoForward}
                loading={isLoadingGetUrlForSignature}
              />
            </div>
          )}
        </div>
      ) : (
        <div className={'signature-area-base-document-module-prospect'}>
          <span>{module.title}</span>
        </div>
      )}
    </Fragment>
  );
};
